/**
 * @file Labs board adapter.
 */

import {
  collection,
  getDocs,
  query,
  where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../../app/firebase/firebase.js";
import {
  appData,
  saveViewSettings,
  state,
} from "../../app/state/state.js";
import { createParentBoardAdapter } from "../../shared/board/parentBoardAdapterFactory.js";
import {
  formatBoardCardDate,
  formatBoardCardDateCompact,
  renderBoardCardChecklistPreview,
  renderBoardCardDateSlot,
  renderBoardCardInfoRow,
  renderBoardCardShell,
  renderBoardCardStatusChip,
  renderBoardCardTypeChip,
  renderBoardCardUserSlot,
} from "../../shared/board/boardCardView.js";
import {
  renderGroupedKanbanBoard,
  renderParentWIPKanbanBoard,
} from "../../shared/board/boardKanbanView.js";
import { applyBoardRuntimePipeline } from "../../shared/board/boardRuntime.js";
import {
  getResolvedCardDate,
  sortCanonicalTaskItems,
} from "../../shared/board/itemSortUtils.js";
import { getBoardTableColumnDefinitions } from "../../shared/board/boardTableColumns.js";
import {
  renderBoardTableHeader,
  renderBoardTableStateCard,
  renderGroupedTableSections,
} from "../../shared/board/boardTableView.js";
import { addBoardTableInteractionListeners } from "../../shared/board/boardTableInteractions.js";
import {
  renderBoardTableTypeBadges,
  renderQuickAddTaskCell,
} from "../../shared/board/boardTableCells.js";
import { renderTaskListHtml } from "../../shared/board/parentBoardCards.js";
import { isItemClosed } from "../../shared/utils/closedState.js";
import {
  formatCurrency,
  isColorDark,
} from "../../shared/utils/displayUtils.js";
import { formatDate } from "../../shared/utils/dateUtils.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
  getPatientProviderDisplayNames,
} from "../../shared/utils/providerUtils.js";
import {
  getUserDisplayNameById,
  getUserFullDisplayName,
  getUserInitials,
} from "../../shared/utils/userDisplay.js";

const LABS_VIEW_KEY = "labs";
const LABS_ITEM_TYPE = "lab";
const WIP_CHECKLIST_LIMIT = 9999;
function getLabsItems() {
  return appData?.labs || [];
}

function getLabsTypesConfig() {
  return appData?.config?.labTypes || [];
}

function getLabsStatusConfig(item) {
  return (appData?.config?.labStatus || [])
    .find((status) => status.id === item?.status) || {};
}

function getPriorityConfig(item) {
  return (appData?.config?.priorities || [])
    .find((priority) => priority.id === item?.priority) || {};
}

function renderBadge(label, color, fallbackClasses = "") {
  const safeLabel = escapeHTML(String(label || ""));
  if (!safeLabel) {return "";}

  if (!color) {
    return `<span class="inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${fallbackClasses}">${safeLabel}</span>`;
  }

  const textColor = isColorDark(color) ? "#ffffff" : "#111827";
  return `<span class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium" style="background-color: ${color}; color: ${textColor};">${safeLabel}</span>`;
}

function getLabLabel(item) {
  const labId = item?.lab || "";
  if (labId === "internal") {
    return appData?.config?.labs?.internalLabLabel || "Internal lab";
  }

  return (appData?.config?.labs?.externalLabs || [])
    .find((lab) => lab?.id === labId)?.name ||
    labId ||
    "";
}

function renderPriorityChip(item) {
  const priority = getPriorityConfig(item);
  return renderBadge(
    priority?.name || item?.priority || "N/A",
    priority?.color || "#e5e7eb",
  );
}

function getAssignedUserDisplay(item) {
  const assigned = Array.isArray(item?.assigned_users) ?
    item.assigned_users.filter(Boolean) :
    [];
  if (assigned.length === 0) {
    return {
      text: "",
      title: "Unassigned",
      name: "",
    };
  }

  const firstUser = (appData?.users || [])
    .find((entry) => entry.id === assigned[0]);
  const names = assigned
    .map((userId) => {
      const user = (appData?.users || [])
        .find((entry) => entry.id === userId);
      return getUserFullDisplayName(user) || getUserDisplayNameById(userId);
    })
    .filter(Boolean);

  return {
    text: assigned.length > 1 ?
      `${getUserInitials(assigned[0]) || "??"}+${assigned.length - 1}` :
      getUserInitials(assigned[0]) || "??",
    title: names.join("\n") || "Assigned user",
    name: getUserFullDisplayName(firstUser) || getUserDisplayNameById(assigned[0]) || "",
  };
}

function getLabsDateTooltip(item, resolvedDate) {
  if (!resolvedDate) {return "";}
  if (isItemClosed(item)) {
    return `Closed: ${formatDate(resolvedDate)}`;
  }
  return `Due: ${formatDate(resolvedDate)}`;
}

function renderLabsCardActionButtons(item) {
  if (!item?.id || isItemClosed(item)) {return "";}
  return `
    <div class="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100">
      <button type="button" class="quick-add-task-btn icon-action-btn kanban-card-action-btn" data-lab-id="${escapeHTML(item.id)}" title="Add Task">
        <span class="material-symbols-outlined pointer-events-none text-[18px] leading-none">add</span>
      </button>
    </div>
  `;
}

function getOpenLabTasks(item, taskList = appData?.tasks || []) {
  const tasks = (taskList || [])
    .filter((task) => task?.parentId === item?.id && !isItemClosed(task));
  return sortCanonicalTaskItems(tasks, taskList || []);
}

function renderLabsTaskListHtml(item, {
  limit = 3,
  taskList = appData?.tasks || [],
} = {}) {
  const tasks = getOpenLabTasks(item, taskList);
  if (tasks.length === 0) {return "";}

  return renderTaskListHtml(tasks, {
    showInitials: true,
    limit,
  });
}

function renderLabsCard(
  item,
  {
    taskLimit = 3,
    checklistLimit = 1,
    taskList = appData?.tasks || [],
  } = {},
) {
  if (!item?.id) {
    return "<div class=\"kanban-card bg-red-100 p-3 rounded-md border-l-4 border-red-500 text-xs\">Invalid Card Data</div>";
  }

  const status = getLabsStatusConfig(item);
  const priority = getPriorityConfig(item);
  const resolvedDate = getResolvedCardDate(LABS_ITEM_TYPE, item, taskList);
  const dateText = formatBoardCardDate(resolvedDate);
  const cardTitle = item.patient_name || "Untitled Lab Item";
  const labLabel = getLabLabel(item);
  const isNotificationsPinnedOpen = !!(
    state.isNotificationShadeOpen &&
    state.viewSettings?.notificationsPinned
  );
  const typeBudget = isNotificationsPinnedOpen ?
    (dateText ? 14 : 46) :
    (dateText ? 22 : 54);
  const taskListHtml = renderLabsTaskListHtml(item, {
    limit: taskLimit,
    taskList,
  });
  const embeddedContentHtml = taskListHtml ?
    `<div class="space-y-1 pt-1">${taskListHtml}</div>` :
    "";
  const infoRowHtml = renderBoardCardInfoRow({
    statusChipHtml: renderBoardCardStatusChip(status),
    typeChipHtml: renderBoardCardTypeChip(
      Array.isArray(item.types) ? item.types : [],
      getLabsTypesConfig(),
      {
        expandToLane: !dateText,
        maxEstimatedUnits: typeBudget,
      },
    ),
    dateHtml: renderBoardCardDateSlot({
      dateText,
      compactDateText: formatBoardCardDateCompact(resolvedDate),
      tooltip: getLabsDateTooltip(item, resolvedDate),
    }),
    userHtml: renderBoardCardUserSlot(getAssignedUserDisplay(item)),
    showUserSlot: true,
    hasEmbeddedContent: Boolean(embeddedContentHtml),
  });

  return renderBoardCardShell({
    itemId: item.id,
    borderColor: priority?.color || status?.color || "#e5e7eb",
    titleHtml: `
      <p class="mb-1 flex min-w-0 items-center gap-1.5 pr-7 text-sm font-semibold text-gray-900 dark:text-gray-200" title="${escapeHTML(cardTitle)}">
        <span class="min-w-0 truncate">${escapeHTML(cardTitle)}</span>
      </p>
    `,
    secondaryTitleHtml: `
      <div class="mb-2 min-w-0">
        <p class="truncate text-xs text-gray-500 dark:text-gray-400" title="${escapeHTML(labLabel)}">${escapeHTML(labLabel)}</p>
      </div>
    `,
    infoRowHtml,
    embeddedContentHtml,
    checklistHtml: renderBoardCardChecklistPreview(item, LABS_ITEM_TYPE, {limit: checklistLimit}),
    actionButtonHtml: renderLabsCardActionButtons(item),
  });
}

async function openLabsDetails(labId) {
  if (!labId) {return;}
  const { showDetails } = await import("../../app/routing/navigationController.js");
  showDetails(labId, LABS_ITEM_TYPE);
}

function attachLabsBoardActionListeners(board) {
  if (!board || board.dataset.labsBoardActionListenerAttached === "true") {return;}
  board.dataset.labsBoardActionListenerAttached = "true";
  board.addEventListener("click", (event) => {
    const quickAddButton = event.target.closest(".quick-add-task-btn");
    if (!quickAddButton?.closest(".kanban-card")) {return;}
    event.preventDefault();
    event.stopPropagation();
    const parentId = quickAddButton.dataset.labId || "";
    if (!parentId) {return;}
    void openLabsTaskForm({
      parentId,
      owner: state.currentUser?.uid,
    });
  });
}

async function loadMoreLabsClosedItems(collectionType) {
  const { loadMoreClosedItems } = await import("../../shared/api/parentItems.js");
  await loadMoreClosedItems(collectionType);
}

async function fetchAllOpenTasksForParentType(parentType) {
  const allTasksQuery = query(
    collection(db, "tasks"),
    where("parentType", "==", parentType),
    where("isSubtask", "==", true),
    where("isClosedResolved", "==", false),
  );
  const snapshot = await getDocs(allTasksQuery);
  return snapshot.docs.map((snapshotDoc) => ({
    id: snapshotDoc.id,
    ...snapshotDoc.data(),
  }));
}

function renderLabsKanban(board) {
  const settings = state.viewSettings?.labs;
  if (!settings) {
    console.error("renderLabsKanban: Labs view settings not loaded.");
    if (board) {
      board.className = "grid grid-cols-1 gap-6";
      board.innerHTML = renderBoardTableStateCard({
        title: "Board unavailable",
        body: "The board settings could not be loaded.",
        tone: "error",
      });
    }
    return;
  }

  const sourceItems = getLabsItems();
  renderGroupedKanbanBoard(board, {
    viewKey: LABS_VIEW_KEY,
    itemType: LABS_ITEM_TYPE,
    singularLabel: "Lab Item",
    sourceItems,
    items: applyBoardRuntimePipeline({
      viewKey: LABS_VIEW_KEY,
      itemType: LABS_ITEM_TYPE,
      items: sourceItems,
      taskList: appData.tasks,
    }),
    groupBy: settings.boardGroupBy,
    dateGroupingMode: settings.boardDateGroupingMode,
    taskList: appData.tasks,
    renderCard: renderLabsCard,
    onLoadMoreClosed: loadMoreLabsClosedItems,
  });
  attachLabsBoardActionListeners(board);
}

function renderLabsWipKanban(board) {
  renderParentWIPKanbanBoard(board, {
    viewKey: LABS_VIEW_KEY,
    itemType: LABS_ITEM_TYPE,
    parentType: LABS_ITEM_TYPE,
    items: getLabsItems(),
    renderCard: (item, openTasks) => renderLabsCard(item, {
      taskLimit: null,
      checklistLimit: WIP_CHECKLIST_LIMIT,
      taskList: openTasks,
    }),
    quickAddDatasetKey: "labId",
    fetchOpenTasksForParentType: fetchAllOpenTasksForParentType,
    onQuickAddTask: (parentId) => openLabsTaskForm({
      parentId,
      owner: state.currentUser?.uid,
    }),
    onOpenDetails: openLabsDetails,
    onLoadMoreClosed: loadMoreLabsClosedItems,
  });
  attachLabsBoardActionListeners(board);
}

function renderUserList(userIds = []) {
  const names = (Array.isArray(userIds) ? userIds : [])
    .map((userId) => getUserDisplayNameById(userId))
    .filter(Boolean);
  return names.length > 0 ?
    escapeHTML(names.join(", ")) :
    "<span class=\"text-xs text-gray-400\">Unassigned</span>";
}

function renderLabsTableCell(item, columnKey) {
  switch (columnKey) {
    case "patient_name": {
      const title = item.patient_name || "Untitled Lab Item";
      return `
        <span class="group flex min-w-0 items-center gap-1.5">
          <span class="min-w-0 truncate font-medium text-gray-800">${escapeHTML(title)}</span>
        </span>
      `;
    }
    case "stream":
      return "";
    case "lab":
      return getLabLabel(item) ?
        escapeHTML(getLabLabel(item)) :
        "<span class=\"text-xs text-gray-400\">None</span>";
    case "types":
      return renderBoardTableTypeBadges(
        Array.isArray(item.types) ? item.types : [],
        getLabsTypesConfig(),
      );
    case "priority":
      return renderPriorityChip(item);
    case "status":
      return renderBoardCardStatusChip(getLabsStatusConfig(item));
    case "provider":
      return escapeHTML(getPatientProviderDisplayNames(item.provider || []).join(", "));
    case "assigned_users":
      return renderUserList(item.assigned_users);
    case "due_date":
      return escapeHTML(formatDate(item.due_date) || "");
    case "cost":
      return item.stream === "external" ?
        escapeHTML(formatCurrency(item.cost) || "") :
        "";
    default:
      return escapeHTML(String(item[columnKey] ?? ""));
  }
}

function renderLabsTableActionCell(item) {
  if (!item?.id || isItemClosed(item)) {
    return "<td class=\"sticky right-0 z-[1] w-12 bg-inherit px-2 py-4 text-center\"></td>";
  }
  return renderQuickAddTaskCell(LABS_ITEM_TYPE, item.id);
}

function stripHtml(html) {
  const element = document.createElement("div");
  element.innerHTML = html;
  return element.textContent || "";
}

function getLabsVisibleColumns(settings = {}) {
  const configuredColumns = Array.isArray(settings.visibleColumns) ?
    settings.visibleColumns :
    [];
  return configuredColumns.filter((column) => column !== "stream");
}

function renderLabsSingleTable(items, itemType, activeFilterKeys = []) {
  const settings = state.viewSettings?.labs;
  if (!settings) {
    console.error("renderLabsSingleTable: Settings not found for labs.");
    return "<p class=\"p-4 text-red-500\">Error: Table settings missing.</p>";
  }

  const visibleColumns = getLabsVisibleColumns(settings);
  const tableHeaderHtml = renderBoardTableHeader({
    visibleColumns,
    columnLabels: getBoardTableColumnDefinitions(LABS_VIEW_KEY),
    columnWidths: settings.columnWidths || {},
    itemType,
    showTaskQuickClose: false,
  });

  const tableBodyHtml = items.length > 0 ?
    `<tbody>
      ${items.map((item) => {
        return `<tr data-id="${item.id}" class="cursor-default border-b bg-white hover:bg-gray-50">
          ${visibleColumns.map((columnKey) => {
            const content = renderLabsTableCell(item, columnKey);
            const titleText = stripHtml(content);
            const titleAttr = titleText.length > 50 ?
              ` title="${escapeHTML(titleText)}"` :
              "";
            const cellClass = columnKey === "types" ?
              "px-6 py-4 align-top" :
              "px-6 py-4";
            return `<td class="${cellClass}"${titleAttr}>${content}</td>`;
          }).join("")}
          ${renderLabsTableActionCell(item)}
        </tr>`;
      }).join("")}
    </tbody>` :
    `<tbody>
      <tr>
        <td colspan="${visibleColumns.length + 1}" class="py-8 text-center text-gray-500">
          No Lab Items found${activeFilterKeys?.length > 0 ? " matching filters" : ""}.
        </td>
      </tr>
    </tbody>`;

  return `<table class="w-full min-w-[920px] table-fixed text-left text-sm text-gray-500 md:min-w-0">
    ${tableHeaderHtml}
    ${tableBodyHtml}
  </table>`;
}

async function openLabsTaskForm(item) {
  const { openParentForm } = await import("../../shared/forms/parentFormModal.js");
  openParentForm("task", item);
}

function renderLabsTable(board) {
  if (!board) {
    console.error("renderLabsTable: Board element not provided.");
    return;
  }

  const settings = state.viewSettings?.labs;
  if (!settings) {
    console.error("renderLabsTable: Labs view settings not loaded.");
    board.className = "";
    board.innerHTML = renderBoardTableStateCard({
      title: "Board unavailable",
      body: "The table settings could not be loaded.",
      tone: "error",
    });
    return;
  }

  const sourceItems = getLabsItems();
  const filteredItems = applyBoardRuntimePipeline({
    viewKey: LABS_VIEW_KEY,
    itemType: LABS_ITEM_TYPE,
    items: sourceItems,
    taskList: appData.tasks,
  });

  board.className = "";
  board.innerHTML = renderGroupedTableSections(filteredItems, {
    viewKey: LABS_VIEW_KEY,
    itemType: LABS_ITEM_TYPE,
    singularLabel: "Lab Item",
    sourceItems,
    groupBy: settings.tableGroupBy,
    dateGroupingMode: settings.tableDateGroupingMode,
    activeFilterKeys: [],
    renderTable: renderLabsSingleTable,
  });

  addBoardTableInteractionListeners(board, {
    viewType: LABS_ITEM_TYPE,
    viewKey: LABS_VIEW_KEY,
    saveViewSettings,
    getTaskItems: () => appData.tasks || [],
    renderTableForView: () => renderLabsTable(board),
    openTaskForm: openLabsTaskForm,
    openDetails: openLabsDetails,
    quickCloseTask: async () => {},
    loadMoreClosedItems: loadMoreLabsClosedItems,
  });
  attachLabsBoardActionListeners(board);
}

/**
 * Returns Labs board items from the app cache.
 * @returns {Array<object>} Cached Labs items.
 */
export function getLabsBoardItems() {
  return getLabsItems();
}

/**
 * Returns whether the Labs board is currently exposed to navigation.
 * @returns {boolean} False until Labs detail routes are enabled.
 */
export function isLabsBoardRuntimeEnabled() {
  return true;
}

export const labsBoardAdapter = createParentBoardAdapter({
  adapterKey: LABS_ITEM_TYPE,
  viewKey: LABS_VIEW_KEY,
  cards: Object.freeze({
    renderCard: renderLabsCard,
  }),
  tableRows: Object.freeze({
    renderCell: renderLabsTableCell,
  }),
  renderers: Object.freeze({
    table: renderLabsTable,
    kanban: renderLabsKanban,
    wipKanban: renderLabsWipKanban,
  }),
});
